import { Link } from 'react-router-dom';
import { Home, KeyRound, BadgeDollarSign, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BRAND } from '@/config/brand';
import { MAP_SALE, MAP_RENT } from '@/config/navLinks';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const AUDIENCES = [
  {
    icon: Home,
    title: 'For buyers',
    desc: 'Explore active listings on an interactive map, filter by what matters, and connect with local agents when you are ready to tour.',
    to: MAP_SALE,
    cta: 'Browse homes for sale',
  },
  {
    icon: KeyRound,
    title: 'For renters',
    desc: 'Find apartments and houses for rent with the same map-first experience — compare options and reach out to listing agents in one place.',
    to: MAP_RENT,
    cta: 'Browse rentals',
  },
  {
    icon: BadgeDollarSign,
    title: 'For sellers',
    desc: "List your home, explore pricing tools, and partner with a seller's agent who knows your market.",
    to: '/dashboard/my-listings',
    cta: 'List your home',
  },
  {
    icon: Users,
    title: 'For agents',
    desc: 'Grow your presence with sponsored placements, lead tools, and a profile that puts your listings in front of serious shoppers.',
    to: '/advertise',
    cta: 'Advertise with us',
  },
];

export default function About() {
  const revealRef = useScrollReveal();

  return (
    <div ref={revealRef}>
      <section className="relative hero-gradient noise-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <p className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-white/80">{BRAND.name}</p>
          <h1 className="font-display mx-auto mt-4 max-w-3xl text-4xl font-bold text-white sm:text-5xl">
            A clearer way to find home
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/85">
            {BRAND.tagline}. We are building a modern real-estate marketplace that helps buyers, renters, sellers, and agents move with confidence.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="font-display text-2xl font-semibold text-slate-900 sm:text-3xl">Our mission</h2>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Placeholder: {BRAND.name} exists to make the home search simpler and more transparent. From map-based discovery to trusted agent connections,
          we aim to put the right listing — and the right people — in front of you at every step. Final company copy will replace this section.
        </p>
      </section>

      <section className="bg-sky-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center">
            <h2 className="font-display text-2xl font-semibold text-slate-900 sm:text-3xl">What we do</h2>
            <p className="mt-2 text-slate-600 max-w-2xl mx-auto">
              Search active MLS-powered listings, save homes you love, and take the next step — whether that is touring, renting, listing, or advertising.
            </p>
          </div>
          <div className="reveal mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {AUDIENCES.map((a) => (
              <div key={a.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-50 text-blue-600">
                  <a.icon size={22} aria-hidden />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{a.title}</h3>
                <p className="mt-2 flex-1 text-sm text-slate-600">{a.desc}</p>
                <Button asChild variant="link" className="mt-4 h-auto justify-start px-0 text-blue-700">
                  <Link to={a.to}>{a.cta}</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="font-display text-2xl font-semibold text-slate-900">Questions?</h2>
        <p className="mt-2 text-slate-600">Visit our Help Center or reach the {BRAND.name} team directly.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button asChild className="rounded-full">
            <Link to="/help">Help Center</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/contact">Contact us</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
